import { useCallback, useEffect, useState } from 'react';
import { STRINGS } from '@/strings/strings';
import { useSceneRegen } from '../../use-scene-regen';

/**
 * RegenImageOverlay — Position-1 Commit-4 (Sebastian rulings
 * 2026-09-04). Option 3 of the Freigabe change dialog: the customer
 * asks for a re-render, optionally with a free-text hint about what
 * should be different.
 *
 * ═══ SHAPE ═════════════════════════════════════════════════════════
 *
 * Multi-line textarea + counter + Neu zeichnen / Abbrechen. Comment
 * is OPTIONAL (empty means "roll the dice again on the same
 * prompt"). Length cap 200 chars, mirrors the scene-text corridor
 * outer bound. Save button enabled as long as the customer has NOT
 * exceeded the cap — an empty comment is a valid save.
 *
 * ═══ MODERATION ════════════════════════════════════════════════════
 *
 * NONE on this surface (removed 2026-09-04). The audio product's
 * moderation prompt asks whether text works as a children's-story
 * TOPIC and returns `invalid_input` on sentences that do not parse as
 * a topic. Customers describe image changes as removals ("keine Tram",
 * "ohne Ballon", "weniger Menschen") — the most common shape of an
 * image correction is not a topic. The gate refused ordinary requests
 * and protected nobody. Content-safety for this surface belongs to
 * the Jugendschutz workstream — the question is "is this safe to draw
 * in a children's book", not "is this a story topic". Tracker:
 * `[jugendschutz-scene-regen-comment]`.
 *
 * The overlay still renders `envelope.user_message` on any server
 * error (cap-reached, wrong-status), same fallback pattern as before.
 *
 * ═══ AFTER SAVE ═════════════════════════════════════════════════════
 *
 * Overlay closes, dialog closes, envelope refetches. The affected
 * page tile switches to `renderState: 'rendering'`. The Freigabe
 * screen polls the envelope every 6 s while any page is rendering
 * (see ReviewScreen). Customer can navigate away — the arrows +
 * thumbnail strip already communicate that; no third sentence in
 * the running label.
 */

// Same 200-char cap the server-side DTO enforces (RegenSceneDto).
// Kept as a client-side constant so the counter can display a hard
// ceiling without an envelope roundtrip; server is authoritative on
// save. Not surfaced as a min — an empty comment is a legitimate
// save (means "roll the dice on the same prompt").
const REGEN_COMMENT_MAX_CHARS = 200;

export interface RegenImageOverlayProps {
  readonly bookOrderId: string;
  readonly sceneId: string;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}

export function RegenImageOverlay({
  bookOrderId,
  sceneId,
  onClose,
  onSaved,
}: RegenImageOverlayProps) {
  const [comment, setComment] = useState('');
  // Position-1 Commit-3 pattern — hold the server-supplied
  // `user_message` when the save fails; fall back to a generic
  // string only when nothing is on the wire.
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const regen = useSceneRegen(bookOrderId);

  const overCap = comment.length > REGEN_COMMENT_MAX_CHARS;

  const handleSave = useCallback(async () => {
    setSaveErrorMessage(null);
    // Empty / whitespace-only comment → send null so the server
    // skips moderation entirely and enqueues a bare regen.
    const trimmed = comment.trim();
    const commentForServer = trimmed.length === 0 ? null : trimmed;
    const result = await regen.save(sceneId, commentForServer);
    if (result.ok) {
      onSaved();
      onClose();
    } else {
      const envelopeMessage = result.error?.envelope?.user_message ?? null;
      setSaveErrorMessage(envelopeMessage ?? STRINGS.book.review.editorSaveError);
    }
  }, [regen, sceneId, comment, onSaved, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="space-y-3">
      <h2 id="page-change-dialog-heading" className="text-lg font-semibold text-dream-navy pr-6">
        {STRINGS.book.review.regenCommentHeading}
      </h2>
      <textarea
        className="builder-field w-full px-3 py-2 text-base text-dream-navy outline-none resize-none"
        rows={4}
        value={comment}
        placeholder={STRINGS.book.review.regenCommentPlaceholder}
        onChange={(e) => {
          setComment(e.target.value);
          if (saveErrorMessage !== null) setSaveErrorMessage(null);
        }}
        aria-label={STRINGS.book.review.regenCommentHeading}
        autoFocus
      />
      {/* Counter: neutral below max, red when over. No min — an empty
          comment is a valid save (customer explicitly asked for a
          fresh render). Mirrors PageChangeDialog's counter grammar
          with min=0. */}
      <p
        className={`text-right text-xs ${overCap ? 'text-error' : 'text-dream-navy/70'}`}
        aria-live="polite"
      >
        {`${comment.length} / ${REGEN_COMMENT_MAX_CHARS}`}
      </p>
      {saveErrorMessage !== null && (
        <p role="alert" className="text-sm text-error">
          {saveErrorMessage}
        </p>
      )}
      <div className="flex gap-3 justify-end pt-1">
        <button
          type="button"
          onClick={onClose}
          disabled={regen.saving}
          className="h-10 rounded-lg border border-dream-navy/30 px-4 text-sm font-semibold text-dream-navy hover:bg-dream-navy/5 disabled:opacity-40"
        >
          {STRINGS.book.review.regenCancelButton}
        </button>
        <button
          type="button"
          onClick={handleSave}
          // Save disabled only when the customer overshoots the
          // client-side max. Empty is legal (no comment, fresh dice
          // roll). Server enforces on save; the local disable is
          // for immediate feedback.
          disabled={regen.saving || overCap}
          className="h-10 rounded-lg bg-dream-navy px-4 text-sm font-semibold text-dream-white hover:bg-dream-navy/90 disabled:opacity-40"
        >
          {STRINGS.book.review.regenSaveButton}
        </button>
      </div>
    </div>
  );
}
